import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';

const AudioNoteRecorder = ({ 
  onRecordingComplete, 
  onCancel,
  maxDuration = 300 // 5 minutes max
}) => {
  const { currentUser } = useAuth();
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');

  const mediaRecorderRef = useRef(null);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // Format time for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Start recording
  const startRecording = async () => {
    console.log('🎤 AudioNoteRecorder: Starting recording...');
    
    try {
      setError('');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      console.log('🎙️ AudioNoteRecorder: Audio stream obtained:', stream.getAudioTracks()[0]?.getSettings());
      
      // iOS Safari compatible MediaRecorder options - same as Record.js
      let recorderOptions = {};
      
      // Pick Safari-friendly MIME types in order of preference
      const preferTypes = [
        'audio/mp4;codecs=mp4a.40.2', // AAC in MP4 (best for iOS)
        'audio/mp4',                   // MP4 (fallback)
        'audio/mpeg'                   // MP3 (alternative)
      ];
      
      const chosenType = preferTypes.find(t => MediaRecorder.isTypeSupported(t)) || '';
      if (chosenType) {
        recorderOptions.mimeType = chosenType;
        console.log('🎙️ AudioNoteRecorder: Using iOS-friendly MIME type:', chosenType);
      } else {
        console.log('🎙️ AudioNoteRecorder: No iOS-friendly MIME type supported, using browser default');
      }
      
      mediaRecorderRef.current = new MediaRecorder(stream, recorderOptions);
      
      const chunks = [];
      mediaRecorderRef.current.ondataavailable = (event) => {
        console.log('🎙️ AudioNoteRecorder: Data available:', event.data.type, event.data.size);
        chunks.push(event.data);
      };
      
      mediaRecorderRef.current.onstop = () => {
        console.log('🛑 AudioNoteRecorder: Recording stopped, processing chunks...');
        const blob = new Blob(chunks, { type: chosenType || 'audio/mp4' });
        console.log('🎵 AudioNoteRecorder: Audio blob created:', {
          type: blob.type,
          size: blob.size,
          duration: recordingTime
        });
        setAudioBlob(blob);
        
        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      };
      
      console.log('🎙️ AudioNoteRecorder: Starting MediaRecorder with options:', recorderOptions);
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      setAudioBlob(null);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 0.1;
          if (newTime >= maxDuration) {
            stopRecording();
            return maxDuration;
          }
          return newTime;
        });
      }, 100);
      
    } catch (error) {
      console.error('❌ AudioNoteRecorder: Error starting recording:', error);
      setError('Failed to start recording. Please check microphone permissions.');
    }
  };

  // Pause/Resume recording
  const togglePause = () => {
    if (!mediaRecorderRef.current || !isRecording) return;
    
    if (isPaused) {
      console.log('▶️ AudioNoteRecorder: Resuming recording...');
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      
      // Resume timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 0.1;
          if (newTime >= maxDuration) {
            stopRecording();
            return maxDuration;
          }
          return newTime;
        });
      }, 100);
    } else {
      console.log('⏸️ AudioNoteRecorder: Pausing recording...');
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      
      // Pause timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  // Redo: discard the current take (recording or paused) and start a fresh one.
  const handleRedo = () => {
    console.log('🔄 AudioNoteRecorder: Redo — discarding current take and restarting');
    if (mediaRecorderRef.current && isRecording) {
      // Skip the normal onstop handler so it doesn't set a stale audioBlob.
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsRecording(false);
    setIsPaused(false);
    setRecordingTime(0);
    setAudioBlob(null);
    setError('');
    startRecording();
  };

  // Re-record: discard the finished preview and return to the tap-to-record screen.
  const handleReRecord = () => {
    console.log('🎙️ AudioNoteRecorder: Re-record — discarding preview');
    setAudioBlob(null);
    setRecordingTime(0);
    setError('');
  };

  // Stop recording
  const stopRecording = () => {
    console.log('🛑 AudioNoteRecorder: Stopping recording...');
    
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
    
    setIsRecording(false);
    setIsPaused(false);
    
    // Clear timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Upload audio to Firebase Storage
  const uploadAudio = async () => {
    if (!audioBlob || !currentUser) {
      console.error('❌ AudioNoteRecorder: No audio blob or user for upload');
      return;
    }

    console.log('☁️ AudioNoteRecorder: Starting audio upload...');
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Generate unique filename
      const timestamp = Date.now();
      const ext = audioBlob.type.includes('mpeg') ? 'mp3' : 'm4a';
      const filename = `study_note_${timestamp}.${ext}`;
      
      console.log('📁 AudioNoteRecorder: Uploading to path:', `study_notes/${currentUser.uid}/${filename}`);
      
      const storageRef = ref(storage, `study_notes/${currentUser.uid}/${filename}`);
      
      // Upload with progress tracking
      const uploadTask = uploadBytes(storageRef, audioBlob, {
        contentType: audioBlob.type
      });
      
      // Note: uploadBytes doesn't provide progress, but we can simulate it
      setUploadProgress(50);
      
      await uploadTask;
      setUploadProgress(100);
      
      const downloadURL = await getDownloadURL(storageRef);
      
      console.log('✅ AudioNoteRecorder: Audio uploaded successfully:', {
        filename,
        url: downloadURL,
        size: audioBlob.size,
        type: audioBlob.type,
        duration: recordingTime
      });
      
      // Call completion callback with audio data
      onRecordingComplete({
        audioUrl: downloadURL,
        audioType: audioBlob.type,
        duration: recordingTime,
        fileSize: audioBlob.size,
        filename
      });
      
    } catch (error) {
      console.error('❌ AudioNoteRecorder: Error uploading audio:', error);
      setError('Failed to upload audio. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Cancel recording
  const handleCancel = () => {
    console.log('❌ AudioNoteRecorder: Cancelling recording...');
    
    // Stop recording if active
    if (isRecording) {
      stopRecording();
    }
    
    // Stop stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    // Clear timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    // Reset state
    setIsRecording(false);
    setIsPaused(false);
    setRecordingTime(0);
    setAudioBlob(null);
    setError('');
    
    onCancel && onCancel();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  return (
    <div className="p-6 bg-white rounded-lg border border-gray-200">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-space-cadet mb-4">Record Audio Note</h3>
        
        {/* Recording Status */}
        <div className="mb-6">
          {isRecording && (
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className={`w-3 h-3 rounded-full ${isPaused ? 'bg-yellow-500' : 'bg-red-500 animate-pulse'}`}></div>
              <span className="text-sm font-medium text-gray-700">
                {isPaused ? 'Paused' : 'Recording'}
              </span>
            </div>
          )}
          
          <div className="text-2xl font-mono font-bold text-space-cadet mb-2">
            {formatTime(recordingTime)}
          </div>
          
          {maxDuration > 0 && (
            <div className="text-xs text-gray-500">
              Max duration: {formatTime(maxDuration)}
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Recording Controls */}
        {!audioBlob && (
          <div className="flex items-center justify-center gap-4 mb-6">
            {!isRecording ? (
              <button
                onClick={startRecording}
                className="w-16 h-16 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
                title="Start recording"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10"/>
                </svg>
              </button>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  onClick={togglePause}
                  className="w-12 h-12 bg-yellow-500 hover:bg-yellow-600 text-white rounded-full flex items-center justify-center transition-colors"
                  title={isPaused ? 'Resume recording' : 'Pause recording'}
                >
                  {isPaused ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                    </svg>
                  )}
                </button>
                
                <button
                  onClick={stopRecording}
                  className="w-12 h-12 bg-gray-500 hover:bg-gray-600 text-white rounded-full flex items-center justify-center transition-colors"
                  title="Stop recording"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="6" y="6" width="12" height="12" rx="2"/>
                  </svg>
                </button>

                <button
                  onClick={handleRedo}
                  className="w-12 h-12 bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-600 rounded-full flex items-center justify-center transition-colors"
                  title="Redo — discard this take and start over"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Audio Preview */}
        {audioBlob && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Audio Preview</h4>
            <audio controls className="w-full" playsInline>
              <source src={URL.createObjectURL(audioBlob)} type={audioBlob.type} />
              Your browser does not support the audio element.
            </audio>
            <button
              onClick={handleReRecord}
              className="mt-3 text-sm text-cerulean hover:text-space-cadet font-medium inline-flex items-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
              </svg>
              Re-record
            </button>
          </div>
        )}

        {/* Upload Progress */}
        {isUploading && (
          <div className="mb-4">
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cerulean"></div>
              <span className="text-sm text-gray-600">Uploading audio...</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-cerulean h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleCancel}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Cancel
          </button>
          
          {audioBlob && !isUploading && (
            <button
              onClick={uploadAudio}
              className="flex-1 px-4 py-2 bg-cerulean text-white rounded-lg hover:bg-space-cadet transition-colors font-medium"
            >
              Save Audio Note
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AudioNoteRecorder;
